import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { X, Upload, Loader2 } from 'lucide-react'
import { createFeedback, uploadFeedbackImage, type FeedbackItem } from '../../lib/feedbackApi'
import { StyledSelect } from '../ui/StyledSelect'

interface FeedbackFormProps {
  open: boolean
  onClose: () => void
  onCreated: (item: FeedbackItem) => void
}

const FEEDBACK_TYPES = [
  { value: '', label: 'None' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'integration', label: 'Integration Request' },
]

export function FeedbackForm({ open, onClose, onCreated }: FeedbackFormProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setTitle('')
      setDescription('')
      setType('')
      setFiles([])
      setError(null)
    }
  }, [open])

  // Handle paste for image upload
  useEffect(() => {
    if (!open) return
    const handler = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file && files.length < 3) {
            setFiles((prev) => [...prev, file].slice(0, 3))
          }
          break
        }
      }
    }
    document.addEventListener('paste', handler)
    return () => document.removeEventListener('paste', handler)
  }, [open, files.length])

  const addFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return
    const validFiles = Array.from(newFiles).filter(
      (f) => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024
    )
    setFiles((prev) => [...prev, ...validFiles].slice(0, 3))
  }, [])

  // Create blob URLs and revoke on cleanup
  const fileUrls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files])
  useEffect(() => {
    return () => { fileUrls.forEach((url) => URL.revokeObjectURL(url)) }
  }, [fileUrls])

  const removeFile = (index: number) => {
    URL.revokeObjectURL(fileUrls[index])
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) {
      setError('Title is required.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const item = await createFeedback({
        title: title.trim(),
        description: description.trim() || undefined,
        type: type || null,
      })

      // Upload images if any
      for (const file of files) {
        try {
          await uploadFeedbackImage(item.id, file)
        } catch {
          // Image upload failure is non-blocking
          console.warn('Failed to upload image:', file.name)
        }
      }

      onCreated(item)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div
      ref={backdropRef}
      onClick={(e) => { if (e.target === backdropRef.current) onClose() }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">New Feedback</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 text-sm bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Brief summary of your feedback"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Description <span className="text-xs text-muted-foreground">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
              rows={5}
              placeholder="Describe your feedback in detail..."
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <StyledSelect
              value={type}
              onChange={setType}
              options={FEEDBACK_TYPES}
            />
          </div>

          {/* Screenshots */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Screenshots <span className="text-xs text-muted-foreground">(up to 3, paste with Ctrl+V)</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {files.map((file, i) => (
                <div key={i} className="relative group">
                  <img
                    src={fileUrls[i]}
                    alt={file.name}
                    className="w-20 h-20 object-cover rounded-lg border border-border"
                  />
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            {files.length < 3 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-border rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
              >
                <Upload className="w-4 h-4" />
                <span>Upload image</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit Feedback
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
